import { type KeyboardEvent, useState } from 'react';
import type { TabItem } from '@/components/custom/arito/form/shared-types';
import { cn } from '@/lib/utils';

interface AritoHeaderTabsProps {
  className?: string;
  tabs: TabItem[];
  defaultTabIndex?: number;
  onTabChange?: (tabId: string) => void;
  actionButtons?: React.ReactNode;
}

export const AritoHeaderTabs = ({
  tabs,
  defaultTabIndex = 0,
  onTabChange,
  className,
  actionButtons
}: AritoHeaderTabsProps) => {
  const [currentTab, setCurrentTab] = useState(defaultTabIndex);

  const selectTab = (tabIndex: number) => {
    if (!tabs[tabIndex]) {
      return;
    }

    setCurrentTab(tabIndex);
    onTabChange?.(tabs[tabIndex].id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabIndex: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab((tabIndex + 1) % tabs.length);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab((tabIndex - 1 + tabs.length) % tabs.length);
    }
  };

  return (
    <div className={className}>
      <div className='flex items-center justify-between border-b border-gray-200'>
        <div
          role='tablist'
          className='relative flex min-h-8 items-end overflow-x-auto after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-gray-200'
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={tabs[currentTab]?.id === tab.id}
              className={cn(
                'relative z-10 min-h-8 min-w-20 whitespace-nowrap px-2 py-0.5 text-[0.7rem] font-black text-gray-500 transition-colors duration-300',
                'after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:origin-left after:scale-x-0 after:bg-main after:transition-transform after:duration-300 hover:bg-main/10 hover:text-main hover:after:scale-x-100',
                tabs[currentTab]?.id === tab.id && 'bg-main/10 text-main after:scale-x-100'
              )}
              onClick={() => selectTab(tabs.indexOf(tab))}
              onKeyDown={event => handleKeyDown(event, tabs.indexOf(tab))}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className='flex items-center'>{actionButtons}</div>
      </div>

      <div>{tabs[currentTab]?.component}</div>
    </div>
  );
};
